import { useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

/**
 * LeftRailTabs
 * - Renders open tabs in a white community-card, below Community Map.
 * - Each tab is a gold-outlined chip with close button.
 * - Clicking a tab resumes or creates a session.
 */
export default function LeftRailTabs({ activeTabId, onActivate, onClosed }) {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadTabs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/tabs/open`, {
        headers: { ...authHeader() },
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Failed to load tabs");
      const data = await resp.json();
      setTabs(
        Array.isArray(data)
          ? data
          : Array.isArray(data.tabs)
          ? data.tabs
          : []
      );
    } catch (e) {
      console.error("Failed to load tabs", e);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  async function handleActivate(tab) {
    try {
      const resp = await fetch(`${API}/frameworks/mindmap/session/by-tab`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        credentials: "include",
        body: JSON.stringify({ tab_id: tab.id }),
      });
      if (!resp.ok) throw new Error(`Resume failed (${resp.status})`);
      const data = await resp.json();

      const sessionId = data.session_id;
      localStorage.setItem("vybly.activeTabId", String(tab.id));
      localStorage.setItem("vybly.activeSessionId", String(sessionId));
      localStorage.setItem("vybly.activeUpdatedAt", String(Date.now()));

      onActivate?.({ ...tab, session_id: sessionId });
    } catch (e) {
      console.error("Could not open tab", e);
      alert("Could not open this tab.");
    }
  }

  async function handleClose(tabId) {
    if (!window.confirm("Close this tab? It will disappear now and be deleted after export.")) return;
    try {
      const resp = await fetch(`${API}/tabs/close/${tabId}`, {
        method: "POST",
        headers: { ...authHeader() },
        credentials: "include",
      });
      if (!resp.ok) throw new Error(`Close failed (${resp.status})`);

      const activeTab = localStorage.getItem("vybly.activeTabId");
      if (activeTab && Number(activeTab) === Number(tabId)) {
        localStorage.removeItem("vybly.activeTabId");
        localStorage.removeItem("vybly.activeSessionId");
        localStorage.removeItem("vybly.activeUpdatedAt");
      }

      onClosed?.(tabId);
      await loadTabs();
    } catch (e) {
      console.error("Could not close tab", e);
      alert("Could not close this tab.");
    }
  }

  useEffect(() => {
    loadTabs();
  }, [loadTabs]);

  return (
    <div className="community-card tabs-card">
      <div className="community-card-header">Your Tabs</div>
      <div className="tabs-instructions">Click a Tab to continue that conversation.</div>
      {loading ? (
        <div className="community-card-body">Loading…</div>
      ) : tabs.length === 0 ? (
        <div className="community-card-body">No open tabs yet.</div>
      ) : (
        <div className="community-card-body tab-chip-container">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`tab-chip ${activeTabId === tab.id ? "active" : ""}`}
            >
              <button
                className="tab-chip-label"
                onClick={() => handleActivate(tab)}
              >
                {tab.custom_label}
              </button>
              <button
                className="tab-chip-close"
                onClick={() => handleClose(tab.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
